<template>
  <div class="top-overlay__wrapper overflow-hidden" :class="{ isActive }">
    <div
      class="box-border relative sm:px-4 px-2 py-[0.675rem] bg-white/80 dark:bg-black/80 border-b border-neutral-200 dark:border-neutral-600 top-overlay__inner"
      style="backdrop-filter: blur(16px)"
    >
      <div class="flex justify-between space-x-4 my-auto">
        <NamiButton
          class="whitespace-nowrap"
          buttonType="primary"
          :href="!shouldShowClose ? seriesHref : undefined"
          :icon="!shouldShowClose ? IconChevronLeft : IconX"
          hideTextOnSmallScreen
          pill
          text
          @buttonClick="handleReturn"
        >
          <div class="hidden sm:block">
            {{ shouldShowClose ? "Close" : "Back to Title" }}
          </div>
        </NamiButton>

        <div
          class="text-center overflow-hidden whitespace-nowrap font-medium my-auto"
          ref="titleWrapper"
        >
          <div class="transition-transform" ref="titleText">
            {{ [seriesTitle, chapterTitle].filter(onlyTruthys).join(", ") }}
          </div>
        </div>

        <div class="flex space-x-2 my-auto">
          <NamiButton
            :button-type="chapterLikeManager?.state ? 'primary' : 'secondary'"
            :icon="chapterLikeManager?.state ? IconHeartFilled : IconHeart"
            pill
            text
            :force-icon-only="!sm && !chapterLikeManager?.likeCount"
            :disabled="
              statsPending ||
              !!chapterLikeManager?.pending ||
              !chapterLikeManager?.ready ||
              solo
            "
            @click.prevent.stop="
              () => chapterLikeManager?.updateState(!chapterLikeManager?.state)
            "
          >
            {{ chapterLikeManager?.likeCount || "Like" }}
          </NamiButton>
          <NamiButton
            v-if="title && !solo"
            buttonType="secondary"
            :icon="IconMessageCircle2"
            hideTextOnSmallScreen
            pill
            :text="!commentsOpen"
            @buttonClick="() => emit('update:commentsOpen', !commentsOpen)"
          >
            {{ currentChapterStats?.attributes.commentCount ?? "" }}
          </NamiButton>
          <NamiButton
            buttonType="secondary"
            :icon="IconMenu2"
            hideTextOnSmallScreen
            pill
            :text="!menuOpen"
            @buttonClick="() => emit('update:menuOpen', !menuOpen)"
          >
            Menu
          </NamiButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  IconChevronLeft,
  IconHeart,
  IconHeartFilled,
  IconMenu2,
  IconMessageCircle2,
  IconX,
} from "@tabler/icons-vue";
import { parseTitleForDisplay } from "~/utils/display/title";
import { parseChapterForDisplay } from "~/utils/display/chapter";
import type { UnwrapNestedRefs } from "vue";
import { ReaderManager } from "~/src/reader/managers/reader";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { getReaderChapter } from "~/src/reader/managers/chapter";
import { isTitle, type TitleEntity } from "~/src/api";
import { useTitleGet } from "~/utils/tanstack/title";
import { getSingleEntityFromCache } from "~/utils/tanstack";

interface Props {
  readerManager: UnwrapNestedRefs<ReaderManager>;
  commentsOpen: boolean;
  menuOpen: boolean;
  isActive?: boolean;
  solo?: boolean;
}

interface Emits {
  (e: "return"): void;

  (e: "toggleLike"): void;

  (e: "update:commentsOpen", v: boolean): void;

  (e: "update:menuOpen", v: boolean): void;
}

const { readerManager, solo } = defineProps<Props>();
const emit = defineEmits<Emits>();
const client = useQueryClient();
const cache = client.getQueryCache();

const { sm } = useBreakpoints();

const currentChapterId = computed(() => readerManager.getCurrChapterId());
const { data: currentChapterData } = getReaderChapter(
  () => client,
  currentChapterId,
);
const { data: currentChapterStats, isPending: statsPending } = useQuery(
  useChapterStats(currentChapterId),
);

const fetchedTitleId = computed(
  () => currentChapterData.value?.relationships.find(isTitle)?.id ?? "",
);

const { data: title } = useQuery({
  ...useTitleGet(fetchedTitleId, ["cover_art", "organization", "tag"]),
  placeholderData: () => {
    if (!fetchedTitleId.value) return;
    return getSingleEntityFromCache(
      cache,
      "title",
      fetchedTitleId.value,
    ) as TitleEntity;
  },
  enabled: computed(() => !!fetchedTitleId.value),
});
const { title: seriesTitle, href: seriesHref } = parseTitleForDisplay(title);

const chapterLikeManager = ref();

watch(
  currentChapterId,
  (id, old) => {
    if (id !== old)
      chapterLikeManager.value = useGlobalChapterLike(() => client, id);
  },
  { immediate: true },
);

const { longName: chapterTitle } = parseChapterForDisplay(currentChapterData);

const titleAreaScrollTimer = ref<NodeJS.Timeout | null>(null);
const titleAreaScrollX = ref(0);
const titleWrapper = ref<HTMLDivElement>();
const titleText = ref<HTMLDivElement>();

onMounted(() => {
  if (!titleWrapper.value || !titleText.value) return;
  if (titleWrapper.value.scrollWidth - titleText.value.clientWidth <= 0) return;

  let overflowScrollEnd = 0;
  let overflowScrollStart = -20;

  titleAreaScrollTimer.value = setInterval(() => {
    if (titleAreaScrollX.value > titleWrapper.value!.scrollWidth) {
      if (overflowScrollEnd < 10) return overflowScrollEnd++;

      overflowScrollStart = -20;
      overflowScrollEnd = 0;
      titleAreaScrollX.value = 0;
      titleText.value!.style.transform = `translateX(0)`;
    }

    if (overflowScrollStart < 0) return overflowScrollStart++;

    titleText.value!.style.transform = `translateX(-${titleAreaScrollX.value}px)`;
    titleAreaScrollX.value += 2;
  }, 100);
});

onBeforeUnmount(() => {
  clearInterval(titleAreaScrollTimer.value!);
});

function handleReturn() {
  if (solo) {
    emit("return");
    return;
  }

  if (title.value) return;
  emit("return");
}

const shouldShowClose = computed(() => !title.value || solo);
</script>

<style scoped lang="postcss">
.top-overlay__wrapper {
  & > .top-overlay__inner {
    transform: translateY(-100%);
    transition: transform 150ms ease-in-out;
  }

  &:hover > .top-overlay__inner {
    @apply canhover:transform-none;
  }

  &.isActive > .top-overlay__inner {
    transform: none;
  }
}
</style>
